import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  IconButton,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const ChineseMarketContentMobileView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const tabContainerRef = useRef(null);

  // Fetch services data with a default value
  const services =
    t("business.chineseMarketSupport.services", {
      returnObjects: true,
    }) ?? [];

  // Handle tab change
  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  // Scroll tabs horizontally
  const scrollTabs = (direction) => {
    const container = tabContainerRef.current;
    if (container) {
      const scrollAmount = direction === "left" ? -200 : 200;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Handle edge cases
  if (services.length === 0) {
    return <Typography>No services available.</Typography>;
  }

  if (activeTab >= services.length) {
    setActiveTab(0); // Reset to the first tab
  }

  // Ensure `lists` is an array
  const serviceFeatures = services[activeTab]?.lists ?? [];

  return (
    <Box>
      {/* Tab Header with Scroll Arrows */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: theme.spacing(3),
        }}
      >
        {/* Left Arrow */}
        <IconButton
          onClick={() => scrollTabs("left")}
          sx={{
            ml: -3,
            display: activeTab > 0 ? "static" : "none",
          }}
        >
          <ArrowBackIosIcon />
        </IconButton>

        {/* Scrollable Tabs */}
        <Box
          ref={tabContainerRef}
          sx={{
            display: "flex",
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            flexGrow: 1,
            margin: theme.spacing(0, 2),
          }}
        >
          {services.map((service, index) => (
            <Box
              key={index}
              onClick={() => handleTabChange(index)}
              sx={{
                flex: "0 0 auto",
                padding: theme.spacing(1, 2),
                fontSize: "0.7rem",
                margin: theme.spacing(0, 1),
                borderRadius: 2,
                backgroundColor:
                  activeTab === index
                    ? theme.palette.primary.main
                    : "transparent",
                color:
                  activeTab === index
                    ? "#ffffff"
                    : theme.palette.text.secondary,
                fontWeight: "bold",
                cursor: "pointer",
                textAlign: "center",
                transition: "background-color 0.3s, color 0.3s",
                "&:hover": {
                  backgroundColor:
                    activeTab === index
                      ? theme.palette.primary.main
                      : theme.palette.action.hover,
                },
              }}
            >
              {service.title}
            </Box>
          ))}
        </Box>

        {/* Right Arrow */}
        <IconButton
          onClick={() => scrollTabs("right")}
          sx={{
            mr: -3,
            display: activeTab < services.length - 1 ? "static" : "none",
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      {/* Tab Content */}
      <Box
        sx={{
          padding: theme.spacing(2),
          backgroundColor: "#f5f5f5",
          borderRadius: 2,
        }}
      >
        {/* Service Title */}
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: "bold",
            color: theme.palette.primary.main,
            fontSize: "1rem",
          }}
        >
          {services[activeTab]?.title}
        </Typography>
        <Divider sx={{ mt: 1 }} />

        {/* Service Features */}
        <List>
          {serviceFeatures.map((feature, idx) => (
            <ListItem
              key={idx}
              sx={{ padding: 0, marginBottom: theme.spacing(1) }}
            >
              <ListItemIcon sx={{ minWidth: "32px" }}>
                <CheckCircleIcon sx={{ color: theme.palette.primary.main }} />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{
                  variant: "body2",
                  color: "text.secondary",
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default ChineseMarketContentMobileView;
